/**
 * Sentiment Analysis Types for Vnitřní Mapa
 *
 * These types define the structure for LLM-powered sentiment analysis
 * of Czech user notes. The schema is designed for i18n - labels are
 * English internally, UI displays Czech translations.
 */

// ============================================================================
// Emotion Types
// ============================================================================

/**
 * Predefined emotions that can be detected in user notes.
 * Limited to 12 common emotions relevant to mood tracking.
 */
export const SENTIMENT_EMOTIONS = [
  'calm',        // klidný
  'focused',     // soustředěný
  'stressed',    // ve stresu
  'frustrated',  // frustrovaný
  'sad',         // smutný
  'tired',       // unavený
  'anxious',     // úzkostný
  'excited',     // nadšený
  'proud',       // hrdý
  'numb',        // otupělý
  'overwhelmed', // přetížený
  'relieved',    // ulevený
] as const

export type SentimentEmotion = typeof SENTIMENT_EMOTIONS[number]

/**
 * Czech translations for emotions (for UI display)
 */
export const EMOTION_LABELS_CS: Record<SentimentEmotion, string> = {
  calm: 'Klidný',
  focused: 'Soustředěný',
  stressed: 'Ve stresu',
  frustrated: 'Frustrovaný',
  sad: 'Smutný',
  tired: 'Unavený',
  anxious: 'Úzkostný',
  excited: 'Nadšený',
  proud: 'Hrdý',
  numb: 'Otupělý',
  overwhelmed: 'Přetížený',
  relieved: 'Ulevený',
}

// ============================================================================
// Trigger Types
// ============================================================================

/**
 * Predefined triggers (causes) that can be detected in user notes.
 * These represent common sources of emotional states.
 */
export const SENTIMENT_TRIGGERS = [
  'sleep',         // spánek
  'work',          // práce
  'studies',       // studium
  'health',        // zdraví
  'relationships', // vztahy
  'money',         // peníze
  'weather',       // počasí
  'unknown',       // neznámé
] as const

export type SentimentTrigger = typeof SENTIMENT_TRIGGERS[number]

/**
 * Czech translations for triggers (for UI display)
 */
export const TRIGGER_LABELS_CS: Record<SentimentTrigger, string> = {
  sleep: 'Spánek',
  work: 'Práce',
  studies: 'Studium',
  health: 'Zdraví',
  relationships: 'Vztahy',
  money: 'Peníze',
  weather: 'Počasí',
  unknown: 'Neznámé',
}

// ============================================================================
// Coping Strategy Types
// ============================================================================

/**
 * Predefined coping strategies that can be detected in user notes.
 * These represent ways users manage their emotional states.
 */
export const COPING_STRATEGIES = [
  'walk',        // procházka
  'exercise',    // cvičení
  'social',      // sociální kontakt
  'rest',        // odpočinek
  'screen_time', // čas u obrazovky
  'avoidance',   // vyhýbání se
  'creative',    // kreativní činnost
  'nature',      // pobyt v přírodě
] as const

export type CopingStrategy = typeof COPING_STRATEGIES[number]

/**
 * Czech translations for coping strategies (for UI display)
 */
export const COPING_LABELS_CS: Record<CopingStrategy, string> = {
  walk: 'Procházka',
  exercise: 'Cvičení',
  social: 'Sociální kontakt',
  rest: 'Odpočinek',
  screen_time: 'Čas u obrazovky',
  avoidance: 'Vyhýbání se',
  creative: 'Kreativní činnost',
  nature: 'Pobyt v přírodě',
}

// ============================================================================
// Red Flag Types (Safety Detection)
// ============================================================================

/**
 * Red flags for safety detection. These are concerning patterns
 * that warrant gentle support suggestions (non-clinical).
 */
export const RED_FLAGS = [
  'hopelessness_language',    // Language suggesting hopelessness
  'isolation_pattern',        // Pattern of social isolation
  'persistent_low_valence',   // Consistently negative over time
  'self_neglect',             // Mentions of neglecting self-care
] as const

export type RedFlag = typeof RED_FLAGS[number]

// ============================================================================
// Direction Type
// ============================================================================

/**
 * Direction of emotional state compared to recent history
 */
export type SentimentDirection = 'improving' | 'declining' | 'stable' | null

// ============================================================================
// Main Sentiment Analysis Interface
// ============================================================================

/**
 * Complete sentiment analysis result from LLM
 * This is stored in the sentiment_data JSONB column
 */
export interface SentimentAnalysis {
  /**
   * Valence: How pleasant/unpleasant the emotional state is
   * Range: -1.0 (very unpleasant) to 1.0 (very pleasant)
   */
  valence: number

  /**
   * Arousal: How activated/calm the emotional state is
   * Range: 0.0 (very calm) to 1.0 (very activated/tense)
   */
  arousal: number

  /**
   * Stress level: Direct measure of stress
   * Range: 0.0 (no stress) to 1.0 (extreme stress)
   */
  stressLevel: number

  /**
   * Detected emotions (max 3)
   * Selected from predefined SENTIMENT_EMOTIONS list
   */
  emotions: SentimentEmotion[]

  /**
   * Detected triggers/causes (max 3)
   * Selected from predefined SENTIMENT_TRIGGERS list
   */
  triggers: SentimentTrigger[]

  /**
   * Detected coping strategies
   * Selected from predefined COPING_STRATEGIES list
   */
  copingStrategies: CopingStrategy[]

  /**
   * Whether sleep was mentioned as relevant factor
   */
  sleepRelated: boolean

  /**
   * Direction compared to recent emotional history
   * null if no prior context available
   */
  direction: SentimentDirection

  /**
   * Safety red flags (conservative detection)
   * Empty array if no concerns detected
   */
  redFlags: RedFlag[]

  /**
   * Confidence in the analysis
   * Range: 0.0 (very uncertain) to 1.0 (very confident)
   */
  confidence: number
}

// ============================================================================
// Aggregation Types (for Pattern Analysis)
// ============================================================================

/**
 * Aggregated sentiment statistics over a time period
 */
export interface SentimentStats {
  /** Average valence across entries */
  avgValence: number

  /** Average arousal across entries */
  avgArousal: number

  /** Average stress level across entries */
  avgStress: number

  /** Trend of valence over the period */
  valenceTrend: 'improving' | 'declining' | 'stable'

  /** Most frequent emotions with counts */
  topEmotions: Array<{
    emotion: SentimentEmotion
    count: number
  }>

  /** Most frequent triggers with counts and average valence when mentioned */
  topTriggers: Array<{
    trigger: SentimentTrigger
    count: number
    avgValenceWhen: number
  }>

  /** Coping strategies and their effectiveness (valence improvement) */
  effectiveCoping: Array<{
    strategy: CopingStrategy
    count: number
    valenceImprovement: number
  }>

  /** Number of red flags detected in the period */
  redFlagCount: number

  /** Total entries analyzed */
  entriesAnalyzed: number

  /** Entries with notes (subset of entriesAnalyzed) */
  entriesWithNotes: number
}

/**
 * Correlation between a trigger and zone levels
 */
export interface TriggerZoneCorrelation {
  trigger: SentimentTrigger
  avgZoneWhenMentioned: number
  avgZoneAfter: number | null
  count: number
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Response from /api/ai/sentiment-insight endpoint
 */
export interface SentimentInsightResponse {
  /** Short Czech insight about the note (1-2 sentences) */
  insight: string

  /** The sentiment analysis data */
  sentiment: SentimentAnalysis | null

  /** Whether analysis is still pending */
  pending: boolean
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Entry with sentiment data (extends DailyEntry from schema)
 */
export interface EntryWithSentiment {
  id: string
  userId: string
  date: string
  zoneId: string
  note: string | null
  timestamp: string
  sentimentData: SentimentAnalysis | null
  valence: number | null
  arousal: number | null
  stressLevel: number | null
  sentimentAnalyzedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

/**
 * Minimal entry data for sentiment analysis context
 */
export interface SentimentContext {
  zoneId: string
  timeOfDay: string
  recentTrend?: number[] // Recent zone values for direction context
}
